import { useState } from "react";
import ReactMarkdown from "react-markdown";
import useUsers from "admin/hooks/useUsers";
import Annotation from "admin/types/interfaces/annotation";
import { getUserInitials, getUserNameByEmail } from "admin/utils/users";
import { MoreHorizontal, Pencil, Send, Trash2, X } from "lucide-react";
import remarkGfm from "remark-gfm";
import { MARKDOWN_COMPONENTS } from "~/components/assistant/lib/markdownComponents";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";

interface AnnotationDrawerProps {
  isOpen: boolean;
  annotations: Annotation[];
  isLoading: boolean;
  onClose: () => void;
  createAnnotation: (content: string) => Promise<Annotation | undefined>;
  updateAnnotation: (annotationId: string, content: string) => Promise<Annotation | undefined>;
  deleteAnnotation: (annotationId: string) => Promise<void>;
}

const MAX_ANNOTATION_CHARS = 10000;

/** The drawer's outer box carries no role of its own, so geometry tests use this. */
const NOTES_DRAWER_TEST_ID = "conversation-notes-drawer";

export function AnnotationDrawer({
  isOpen,
  annotations,
  isLoading,
  onClose,
  createAnnotation,
  updateAnnotation,
  deleteAnnotation,
}: AnnotationDrawerProps) {
  const { showError } = useToast();
  const { data: users } = useUsers();

  // Helper to get user by email
  const getUserByEmail = (email: string) => users?.find((u) => u.email === email);

  // State management
  const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);
  // editContent: current textarea value; originalEditContent: snapshot at edit start
  // Both needed to detect changes and enable cancel/rollback without re-querying annotations
  const [editContent, setEditContent] = useState("");
  const [originalEditContent, setOriginalEditContent] = useState("");
  const [newAnnotationContent, setNewAnnotationContent] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Handle edit mode
  const handleEdit = (annotation: Annotation) => {
    setEditingAnnotationId(annotation.id);
    setEditContent(annotation.content);
    setOriginalEditContent(annotation.content);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingAnnotationId(null);
    setEditContent("");
    setOriginalEditContent("");
  };

  // Handle save edit
  const handleSaveEdit = async (annotationId: string) => {
    if (!editContent.trim() || editContent.length > MAX_ANNOTATION_CHARS) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateAnnotation(annotationId, editContent);
      setEditingAnnotationId(null);
      setEditContent("");
      setOriginalEditContent("");
    } catch (error) {
      showError("Failed to update note");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async (annotationId: string) => {
    try {
      await deleteAnnotation(annotationId);
    } catch (error) {
      showError("Failed to delete note");
      throw error;
    }
  };

  // Handle create annotation
  const handleCreateAnnotation = async () => {
    if (!newAnnotationContent.trim() || newAnnotationContent.length > MAX_ANNOTATION_CHARS) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createAnnotation(newAnnotationContent);
      setNewAnnotationContent("");
    } catch (error) {
      showError("Failed to create note");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if content is valid
  const isEditContentValid =
    editContent.trim().length > 0 && editContent.length <= MAX_ANNOTATION_CHARS;
  const isNewContentValid =
    newAnnotationContent.trim().length > 0 && newAnnotationContent.length <= MAX_ANNOTATION_CHARS;
  const canSendNote = isNewContentValid && !isSubmitting;

  // Check if edit content changed
  const editContentChanged = editContent !== originalEditContent;

  return (
    <>
      {/* Kept mounted while collapsed so an unsaved draft, an in-progress edit
          and the delete confirmation all survive a collapse. `hidden` keeps that
          content out of the tab order and the a11y tree meanwhile, at the price
          of an unanimated collapse: it lands in the same commit that starts the
          column narrowing, so what slides shut is an empty column.
          `min-w-[363px]` is the column's content width (its 364px less its left
          border), so the column clips these notes as it slides rather than
          re-wrapping them at each intermediate width. */}
      <div
        data-testid={NOTES_DRAWER_TEST_ID}
        className={cn(isOpen ? "flex min-h-0 min-w-[363px] flex-1 flex-col" : "hidden")}
      >
        <div className="bg-background flex flex-col h-full">
          <div className="shrink-0 border-b border-border px-4 pt-3.5 pb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Notes</h2>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={onClose}
              aria-label="Close notes"
            >
              <X className="size-3.5" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* N.B. `h-full` on both placeholders: `justify-center` centres within
                the box's own height, so without it these sit at the top of the
                scroll area with 48px of padding and read as misaligned. */}
            {isLoading && (annotations?.length ?? 0) === 0 ? (
              <div className="flex h-full items-center justify-center py-12">
                <div className="text-xs text-muted-foreground">Loading notes...</div>
              </div>
            ) : (annotations?.length ?? 0) === 0 ? (
              <div className="flex h-full flex-col items-center justify-center py-12 text-center">
                <Pencil className="size-8 text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground">No notes yet</p>
              </div>
            ) : (
              [...(annotations ?? [])]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((annotation) => {
                  const isEditing = editingAnnotationId === annotation.id;
                  const user = getUserByEmail(annotation.adminUserId);

                  return (
                    <div key={annotation.id} className="border-b border-border">
                      <Card
                        variant="flat"
                        className="px-4 py-3 gap-0 group hover:bg-neutral-50 transition-colors"
                      >
                        <CardHeader className="p-0 gap-0">
                          <CardTitle className="flex items-center gap-2 text-xs font-normal">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={user?.image_url} />
                              <AvatarFallback className="text-xs">
                                {user && getUserInitials(user)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-semibold">
                              {getUserNameByEmail(annotation.adminUserId, users ?? [])}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {formatTimestamp(annotation.createdAt)}
                            </span>
                          </CardTitle>
                          <CardAction>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <MoreHorizontal className="size-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(annotation)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setDeleteConfirmId(annotation.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </CardAction>
                        </CardHeader>

                        <CardContent className="p-0">
                          {isEditing ? (
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="min-h-24 text-xs md:text-xs"
                              placeholder="Edit note..."
                            />
                          ) : (
                            <div className="flex items-start gap-1">
                              {/* N.B. three overrides `prose` makes necessary here.
                                  `prose-sm` sets its own 14px on the wrapper, so
                                  `text-xs` has to sit beside it to land 12px on the
                                  paragraphs. `MARKDOWN_COMPONENTS` hardcodes
                                  `text-sm` on inline `code` for every markdown
                                  surface in the app, so only a descendant variant
                                  brings it down to the body it sits in. And `prose`
                                  prints literal backticks around `code` through
                                  `content`, which the transcript's own chips - same
                                  renderer, no `prose` - do not have. */}
                              <div className="text-xs [&_code]:text-xs prose prose-sm prose-code:before:content-none prose-code:after:content-none max-w-none text-foreground">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={MARKDOWN_COMPONENTS}
                                >
                                  {annotation.content}
                                </ReactMarkdown>
                              </div>
                              {new Date(annotation.updatedAt).getTime() !==
                                new Date(annotation.createdAt).getTime() && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      {/* `shrink-0`: this shares a flex row with a
                                          body that wants every pixel, and
                                          `overflow-wrap: anywhere` applies, so
                                          otherwise it is squeezed until it breaks
                                          mid-word into "(Edit" / "ed)". */}
                                      <span className="shrink-0 text-[10px] text-muted-foreground cursor-default">
                                        (Edited)
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{formatTimestamp(annotation.updatedAt)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          )}
                        </CardContent>

                        {isEditing && (
                          <CardFooter className="flex justify-end gap-2 p-0 pt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={handleCancelEdit}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs"
                              onClick={() => handleSaveEdit(annotation.id)}
                              disabled={!isEditContentValid || !editContentChanged || isSubmitting}
                              loading={isSubmitting}
                            >
                              Save
                            </Button>
                          </CardFooter>
                        )}
                      </Card>
                    </div>
                  );
                })
            )}
          </div>

          <div className="border-t border-border bg-background p-4 shrink-0">
            <div className="relative">
              {/* One line at rest, four while it is being written in. `min-h-0` is
                  what makes `h-9` reachable at all: the base sets `min-h-16`, and
                  `cn`'s `twMerge` only drops that floor because this string names
                  a conflicting `min-h-*`. An explicit height still beats the base's
                  `field-sizing-content`, so a long draft scrolls rather than growing.
                  The height stays pinned while the field holds text so it cannot
                  collapse under the writer as focus moves to the send button. */}
              <Textarea
                value={newAnnotationContent}
                onChange={(e) => setNewAnnotationContent(e.target.value)}
                placeholder="Add a note..."
                className={cn(
                  "resize-none pr-10 text-xs md:text-xs transition-[color,box-shadow,height]",
                  newAnnotationContent ? "h-24" : "h-9 min-h-0 focus:h-24"
                )}
              />
              {/* A 4px corner, not the usual 8px: that is what centres a `size-7`
                  disc in the collapsed 36px field, and squaring the inset keeps it
                  off the bottom border once the field expands.
                  N.B. `bg-gray-500` at full opacity is the dead state, because
                  `bg-muted` under the base's `disabled:opacity-50` composites to
                  1.04:1 against the field behind it - invisible, on the panel's
                  only submit control. It is gated on the draft rather than applied
                  unconditionally: naming any `bg-*`/`text-*` while a send is in
                  flight makes `twMerge` drop the `loading` variant's own fill and
                  its hidden glyph, painting the spinner and the send icon on top of
                  each other. */}
              <Button
                size="icon"
                className={cn(
                  "absolute right-1 bottom-1 size-7 rounded-full disabled:opacity-100",
                  !isNewContentValid && "bg-gray-500"
                )}
                onClick={handleCreateAnnotation}
                disabled={!canSendNote}
                loading={isSubmitting}
                aria-label="Add note"
              >
                <Send className="size-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDestructiveDialog
        target={deleteConfirmId}
        title="Delete Note?"
        description={() => "This action cannot be undone. This will permanently delete the note."}
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteConfirmId(null)}
      />
    </>
  );
}
